import React, { useState, useEffect } from 'react';
import { X, Clock, DollarSign, Users, Globe, ToggleLeft, ToggleRight } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

export default function ServiceModal({ service, categories, onSave, onClose }) {
  const { user, isSuperAdmin, profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [staffMembers, setStaffMembers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    categoryId: '',
    duration: 60,
    price: '',
    bufferTime: 0,
    isActive: true,
    onlineBookingEnabled: true,
    staffIds: []
  });

  // Get tenant_id - Super Admins can view all (pass null), others use their tenant
  const tenantId = isSuperAdmin() ? null : (profile?.tenant_id || user?.user_metadata?.tenant_id || user?.raw_user_meta_data?.tenant_id);

  useEffect(() => {
    loadStaffMembers();
    if (service) {
      setFormData({
        name: service?.name || '',
        description: service?.description || '',
        categoryId: service?.categoryId || '',
        duration: service?.duration || 60,
        price: service?.price?.toString() || '',
        bufferTime: service?.bufferTime || 0,
        isActive: service?.isActive !== false,
        onlineBookingEnabled: service?.onlineBookingEnabled !== false,
        staffIds: service?.assignedStaff?.map(s => s?.id) || []
      });
    }
  }, [service]);

  const loadStaffMembers = async () => {
    try {
      let query = supabase?.from('user_profiles')?.select('id, first_name, last_name, email');
      
      // Only filter by tenant_id if provided (Super Admins pass null to see all staff)
      if (tenantId) {
        query = query?.eq('tenant_id', tenantId);
      }
      
      const { data, error } = await query?.eq('is_active', true)?.order('first_name');

      if (error) throw error;
      setStaffMembers(data || []);
    } catch (err) {
      console.error('Error loading staff members:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const toggleStaffMember = (staffId) => {
    setFormData(prev => ({
      ...prev,
      staffIds: prev?.staffIds?.includes(staffId)
        ? prev?.staffIds?.filter(id => id !== staffId)
        : [...prev?.staffIds, staffId]
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    try {
      await onSave(formData);
    } catch (err) {
      setError(err?.message);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {service?.id ? 'Edit Service' : 'New Service'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData?.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter service name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData?.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter service description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                name="categoryId"
                value={formData?.categoryId}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select category</option>
                {categories?.map(cat => (
                  <option key={cat?.id} value={cat?.id}>
                    {cat?.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                Duration (minutes) *
              </label>
              <input
                type="number"
                name="duration"
                value={formData?.duration}
                onChange={handleChange}
                required
                min="1"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <DollarSign className="w-4 h-4 inline mr-1" />
                Price
              </label>
              <input
                type="number"
                name="price"
                value={formData?.price}
                onChange={handleChange}
                step="0.01"
                min="0"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Buffer Time (minutes)
              </label>
              <input
                type="number"
                name="bufferTime"
                value={formData?.bufferTime}
                onChange={handleChange}
                min="0"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Users className="w-4 h-4 inline mr-1" />
              Assign Staff Members
            </label>
            <div className="border border-gray-300 rounded-lg p-4 max-h-48 overflow-auto">
              {staffMembers?.length === 0 ? (
                <p className="text-sm text-gray-500">No staff members available</p>
              ) : (
                <div className="space-y-2">
                  {staffMembers?.map(staff => (
                    <label
                      key={staff?.id}
                      className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={formData?.staffIds?.includes(staff?.id)}
                        onChange={() => toggleStaffMember(staff?.id)}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-900">
                        {staff?.first_name} {staff?.last_name}
                      </span>
                      <span className="text-xs text-gray-500 ml-auto">
                        {staff?.email}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="isActive"
                checked={formData?.isActive}
                onChange={handleChange}
                className="sr-only"
              />
              <div className="flex items-center gap-2">
                {formData?.isActive ? (
                  <ToggleRight className="w-6 h-6 text-green-600" />
                ) : (
                  <ToggleLeft className="w-6 h-6 text-gray-400" />
                )}
                <span className="text-sm font-medium text-gray-700">
                  Active Service
                </span>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="onlineBookingEnabled"
                checked={formData?.onlineBookingEnabled}
                onChange={handleChange}
                className="sr-only"
              />
              <div className="flex items-center gap-2">
                <Globe className={`w-5 h-5 ${formData?.onlineBookingEnabled ? 'text-blue-600' : 'text-gray-400'}`} />
                <span className="text-sm font-medium text-gray-700">
                  Online Booking Enabled
                </span>
              </div>
            </label>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-700 hover:text-gray-900 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Service'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}